import { z } from 'zod';
import { auditFieldsSchema, dataOraSchema, idSchema, paginationQuerySchema } from '../common';
import { regoleFasiSchema } from '../progetti';

/**
 * Il template è uno STAMPO: fasi tipiche + struttura documentale a slot
 * vuoti. Alla creazione del progetto viene COPIATO; da quel momento template e
 * progetto sono indipendenti.
 */

export const templateCreateSchema = z.object({
  /** Univoco: diventa il tag-tipologia del progetto. */
  nome: z.string().trim().min(1).max(255),
  descrizione: z.string().trim().max(10_000).optional(),
  attivo: z.boolean().default(true),
  regoleFasi: regoleFasiSchema.default({
    abilitaCreazioneFasi: true,
    permettiEliminazioneFasi: true,
    permettiModificaFasi: true,
  }),
});
export type TemplateCreate = z.infer<typeof templateCreateSchema>;

export const templateUpdateSchema = templateCreateSchema.partial();
export type TemplateUpdate = z.infer<typeof templateUpdateSchema>;

export const templateSchema = templateCreateSchema.extend({
  id: idSchema,
  ...auditFieldsSchema.shape,
});
export type Template = z.infer<typeof templateSchema>;

/** Riga della tabella template: il taglio che serve a scansionare l'elenco. */
export const templateListItemSchema = z.object({
  id: idSchema,
  nome: z.string(),
  descrizione: z.string().nullable(),
  attivo: z.boolean(),
  nFasi: z.number().int(),
  creatoIl: dataOraSchema,
});
export type TemplateListItem = z.infer<typeof templateListItemSchema>;

export const templateListQuerySchema = paginationQuerySchema.extend({
  attivo: z.coerce.boolean().optional(),
});
export type TemplateListQuery = z.infer<typeof templateListQuerySchema>;

export const templateFaseCreateSchema = z.object({
  nome: z.string().trim().min(1).max(255),
  descrizione: z.string().trim().max(10_000).optional(),
  ordine: z.coerce.number().int().min(0),
  riservata: z.boolean().default(false),
});
export type TemplateFaseCreate = z.infer<typeof templateFaseCreateSchema>;

/** Struttura documentale dello stampo: blocco → cartelle (max 1 livello) → slot. */
export const templateSlotCreateSchema = z.object({
  nome: z.string().trim().min(1).max(255),
  obbligatorio: z.boolean().default(false),
  ordine: z.coerce.number().int().min(0).default(0),
});
export type TemplateSlotCreate = z.infer<typeof templateSlotCreateSchema>;

export const templateCartellaCreateSchema = z.object({
  nome: z.string().trim().min(1).max(255),
  ordine: z.coerce.number().int().min(0).default(0),
  slot: z.array(templateSlotCreateSchema).default([]),
});
export type TemplateCartellaCreate = z.infer<typeof templateCartellaCreateSchema>;

/**
 * Il blocco è "una stanza" per contesto: al più uno per template e al più
 * uno per fase. Per questo l'appartenenza (template vs fase) non è una FK nel
 * payload, ma la POSIZIONE nell'albero — annidato dentro `templateFaseCreateSchema`
 * per il blocco di fase, o `documentiGenerali` su `templateCompletoSchema` per
 * quello di progetto. Una FK esplicita per id non funzionerebbe comunque: una
 * fase appena aggiunta nello stesso salvataggio non ha ancora un id da referenziare.
 *
 * Niente `nome` in questo payload: a differenza di cartella/slot (che possono
 * essere più d'uno e vanno distinti), il blocco è unico nel suo contesto — non
 * serve mai a un utente distinguerlo da un fratello che non ha. Il nome persiste
 * a DB (colonna NOT NULL) ma è il server a derivarlo dal contesto (nome fase, o
 * "Documenti di progetto"), non un input dell'editor.
 */
export const templateBloccoFileCreateSchema = z.object({
  cartelle: z.array(templateCartellaCreateSchema).default([]),
  slot: z.array(templateSlotCreateSchema).default([]),
});
export type TemplateBloccoFileCreate = z.infer<typeof templateBloccoFileCreateSchema>;

export const templateFaseConDocumentiCreateSchema = templateFaseCreateSchema.extend({
  /** Blocco file di questa fase, se presente (al più uno per fase). */
  documenti: templateBloccoFileCreateSchema.optional(),
});
export type TemplateFaseConDocumentiCreate = z.infer<typeof templateFaseConDocumentiCreateSchema>;

/**
 * Payload completo dell'editor di template (fasi + struttura documentale).
 * Sostituisce l'intera struttura ad ogni salvataggio (nessun `id` da riconciliare
 * lato client): al volume di un template — decine di fasi/slot al più — un
 * cancella-e-reinserisci in transazione è più semplice e ovviamente corretto di
 * un diff per id, e nessun'altra tabella referenzia `template_fase`/
 * `template_blocco_file`, quindi rigenerare gli id ad ogni salvataggio è sicuro.
 */
export const templateCompletoSchema = templateCreateSchema.extend({
  fasi: z.array(templateFaseConDocumentiCreateSchema).default([]),
  /** Blocco "documenti di progetto", generale — non legato a nessuna fase. */
  documentiGenerali: templateBloccoFileCreateSchema.optional(),
});
export type TemplateCompleto = z.infer<typeof templateCompletoSchema>;

/**
 * Solo la struttura (fasi + documenti), senza i dati base del template: è il
 * corpo di `PUT /api/template/:id/struttura` — i dati base si aggiornano a
 * parte con `templateUpdateSchema` (`PATCH /api/template/:id`).
 */
export const templateStrutturaCreateSchema = templateCompletoSchema.pick({
  fasi: true,
  documentiGenerali: true,
});
export type TemplateStrutturaCreate = z.infer<typeof templateStrutturaCreateSchema>;

/* ── Viste (con id, per il dettaglio/editor) ───────────────────────────────
 * Stessa forma dei payload di creazione, con l'`id` di ogni riga. Non sono ciò
 * che il client rimanda al salvataggio: l'editor le usa solo per
 * mostrare lo stato attuale (`:key` stabile per il drag&drop, ecc.).
 */

export const templateSlotSchema = templateSlotCreateSchema.extend({ id: idSchema });
export type TemplateSlot = z.infer<typeof templateSlotSchema>;

export const templateCartellaSchema = templateCartellaCreateSchema
  .omit({ slot: true })
  .extend({ id: idSchema, slot: z.array(templateSlotSchema) });
export type TemplateCartella = z.infer<typeof templateCartellaSchema>;

export const templateBloccoFileSchema = templateBloccoFileCreateSchema
  .omit({ cartelle: true, slot: true })
  .extend({
    id: idSchema,
    /** Derivato dal server (nome fase, o "Documenti di progetto"): sopra. */
    nome: z.string(),
    cartelle: z.array(templateCartellaSchema),
    slot: z.array(templateSlotSchema),
  });
export type TemplateBloccoFile = z.infer<typeof templateBloccoFileSchema>;

export const templateFaseSchema = templateFaseCreateSchema.extend({
  id: idSchema,
  documenti: templateBloccoFileSchema.optional(),
});
export type TemplateFase = z.infer<typeof templateFaseSchema>;

/** Dettaglio completo di un template: dati base + l'intero albero (`GET /api/template/:id`). */
export const templateDettaglioSchema = templateSchema.extend({
  fasi: z.array(templateFaseSchema).default([]),
  documentiGenerali: templateBloccoFileSchema.optional(),
});
export type TemplateDettaglio = z.infer<typeof templateDettaglioSchema>;
